import AppConstants from '../../components/App/constants';
import { FEATURE_TOGGLES } from '../../modules/feature-toggles/feature-toggles';
import { FeatureTogglesProp } from '../../modules/feature-toggles/get-server-side-feature-toggle-props';
import { useRedux } from '../modularRedux';
import reducers from './reducers';

export const storeKey = 'toggles';

export interface TogglesState {
  hideFunding: {
    hidePlaid: boolean;
    hideManualACH: boolean;
    hideDebit: boolean;
  };
  enhancedDebit: {
    applePayEnabled: boolean;
    googlePayEnabled: boolean;
    threeDSEnabled: boolean;
    paypalEnabled: boolean;
    venmoEnabled: boolean;
  };
  walletMinimum: {
    shouldShift: boolean;
    amount: number;
    displayAmount: number;
  };
  plaidOAuthManualOverrideEnabled: boolean;
  plaidOAuthAndroidLinkEnabled: boolean;
  isFbPixelEnabled: boolean;
  accessManagementEnabled: boolean;
  promoCodeVerificationEnabled: boolean;
  preferNotToSayOptionEnabled: boolean;
  requireTokenForApproversEnabled: boolean;
  shouldBounceInvitedUsersWhoDidntUseInviteLink: boolean;
  proveMobileAuthEnabled: boolean;
  noPhoneNumberPriceText: boolean;
  myToggleEnabled: boolean;
  unauthorizedCleanStateEnabled: boolean;
  emailBlurEnabled: boolean;
  smsNotificationsEnabled: boolean;
  smsNotificationsDefaultChecked: boolean;
  creditWaitlistEnabled: boolean;
  creditWaitlistShowConfirmScreen: boolean;
  personasServiceEnabled: FeatureTogglesProp['personasServiceEnabled'];
  planFlow: FeatureTogglesProp['planFlow'];
  modularReduxEnabled: FeatureTogglesProp['modularReduxEnabled'];
  featureToggles: Record<string, any>; // @todo: merge these instead of adding them as a property
  roleSelectCTA: FeatureTogglesProp['roleSelectCTA'];
  uspsValidationUnblock: FeatureTogglesProp['uspsValidationUnblock'];
  improvedDownloadScreen: FeatureTogglesProp['improvedDownloadScreen'];
  challengeRequested3dsEnabled: FeatureTogglesProp['challengeRequested3dsEnabled'];
  preventNonUSTimezones: FeatureTogglesProp['preventNonUSTimezones'];
  acquisitionRoleBasedRedirect: FeatureTogglesProp['acquisitionRoleBasedRedirect'];
  growthRegistrationFlowFields: FeatureTogglesProp['growthRegistrationFlowFields'];
  acquisitionSetPlanMaybeLater: FeatureTogglesProp['acquisitionSetPlanMaybeLater'];
  defaultInfinity: FeatureTogglesProp['defaultInfinity'];
  defaultMax: FeatureTogglesProp['defaultMax'];
  ltaResetPasswordExperience: FeatureTogglesProp['ltaResetPasswordExperience'];
  kycRetryEnabled: FeatureTogglesProp['kycRetryEnabled'];
  reOrderDebitOnFundingScreen: FeatureTogglesProp['reOrderDebitOnFundingScreen'];
  parentFundWalletDesign: FeatureTogglesProp['parentFundWalletDesign'];
  onboardingNewUserOnboarding: FeatureTogglesProp['onboardingNewUserOnboarding'];
  corePlanCopy: FeatureTogglesProp['corePlanCopy'];
  kidFirstRegParentInviteV0: FeatureTogglesProp['kidFirstRegParentInviteV0'];
  kidFirstRegParentInviteV1: FeatureTogglesProp['kidFirstRegParentInviteV1'];
  enableCorePriceChange: FeatureTogglesProp['enableCorePriceChange'];
  enableGSRChange: FeatureTogglesProp['enableGSRChange'];
  linkToExternalBrowserWithBadWriaUrl: FeatureTogglesProp['linkToExternalBrowserWithBadWriaUrl'];
  riskFreePaintedDoor: FeatureTogglesProp['riskFreePaintedDoor'];
}

export const initialState: TogglesState = {
  hideFunding: {
    hidePlaid: false,
    hideManualACH: false,
    hideDebit: false
  },
  enhancedDebit: {
    applePayEnabled: false,
    googlePayEnabled: false,
    threeDSEnabled: false,
    paypalEnabled: false,
    venmoEnabled: false
  },
  walletMinimum: {
    shouldShift: false,
    amount: AppConstants.MINIMUM_WALLET_LOAD.defaultMinimumAmount || 20.0,
    displayAmount: AppConstants.MINIMUM_WALLET_LOAD.defaultDisplayMinimumAmount
  },
  plaidOAuthManualOverrideEnabled: false,
  plaidOAuthAndroidLinkEnabled: false,
  isFbPixelEnabled: false,
  accessManagementEnabled: false,
  promoCodeVerificationEnabled: false,
  preferNotToSayOptionEnabled: false,
  requireTokenForApproversEnabled: false,
  shouldBounceInvitedUsersWhoDidntUseInviteLink: false,
  proveMobileAuthEnabled: false,
  noPhoneNumberPriceText: false,
  myToggleEnabled: false,
  unauthorizedCleanStateEnabled: false,
  emailBlurEnabled: false,
  smsNotificationsEnabled: false,
  smsNotificationsDefaultChecked: false,
  creditWaitlistEnabled: false,
  creditWaitlistShowConfirmScreen: false,
  personasServiceEnabled: FEATURE_TOGGLES.personasServiceEnabled.default,
  planFlow: FEATURE_TOGGLES.planFlow.default,
  modularReduxEnabled: FEATURE_TOGGLES.modularReduxEnabled.default,
  roleSelectCTA: FEATURE_TOGGLES.roleSelectCTA.default,
  uspsValidationUnblock: FEATURE_TOGGLES.uspsValidationUnblock.default,
  improvedDownloadScreen: FEATURE_TOGGLES.improvedDownloadScreen.default,
  challengeRequested3dsEnabled:
    FEATURE_TOGGLES.challengeRequested3dsEnabled.default,
  preventNonUSTimezones: FEATURE_TOGGLES.preventNonUSTimezones.default,
  acquisitionRoleBasedRedirect:
    FEATURE_TOGGLES.acquisitionRoleBasedRedirect.default,
  growthRegistrationFlowFields:
    FEATURE_TOGGLES.growthRegistrationFlowFields.default,
  acquisitionSetPlanMaybeLater:
    FEATURE_TOGGLES.acquisitionSetPlanMaybeLater.default,
  defaultMax: FEATURE_TOGGLES.defaultMax.default,
  defaultInfinity: FEATURE_TOGGLES.defaultInfinity.default,
  ltaResetPasswordExperience:
    FEATURE_TOGGLES.ltaResetPasswordExperience.default,
  kycRetryEnabled: FEATURE_TOGGLES.kycRetryEnabled.default,
  reOrderDebitOnFundingScreen:
    FEATURE_TOGGLES.reOrderDebitOnFundingScreen.default,
  parentFundWalletDesign: FEATURE_TOGGLES.parentFundWalletDesign.default,
  onboardingNewUserOnboarding:
    FEATURE_TOGGLES.onboardingNewUserOnboarding.default,
  corePlanCopy: FEATURE_TOGGLES.corePlanCopy.default,
  kidFirstRegParentInviteV0: FEATURE_TOGGLES.kidFirstRegParentInviteV0.default,
  linkToExternalBrowserWithBadWriaUrl:
    FEATURE_TOGGLES.linkToExternalBrowserWithBadWriaUrl.default,
  kidFirstRegParentInviteV1: FEATURE_TOGGLES.kidFirstRegParentInviteV1.default,
  enableCorePriceChange: FEATURE_TOGGLES.enableCorePriceChange.default,
  enableGSRChange: FEATURE_TOGGLES.enableGSRChange.default,
  riskFreePaintedDoor: FEATURE_TOGGLES.riskFreePaintedDoor.default,
  featureToggles: {} // @todo: merge these instead of adding them as a property
};

export const [useToggles, useToggleDispatchers, toggleActions] = useRedux(
  storeKey,
  reducers,
  initialState
);
